import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import type { StepperContextValue } from "@/components/stepper/useStepper";

import { toast } from "@/components/toast/toast-queue";
import { useCreateBrandFormContext } from "@/features/brands/components/BrandsForm";
import { useCreateBrand } from "@/features/brands/shared/hooks/useCreateBrand";
import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";
import { useStartScrape } from "@/features/brands/shared/hooks/useStartScrape";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

function normalizeUrl(url: string): string {
  if (!url) return url;

  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export function useBrandCreationFlow(stepper: StepperContextValue): {
  handleStep1Next: () => Promise<void>;
  handleStep2Next: () => Promise<void>;
} {
  const navigate = useNavigate();
  const projectId = useGetCurrentProjectId();
  const form = useCreateBrandFormContext();
  const { mutateAsync: createBrand } = useCreateBrand();
  const { mutateAsync: startScrape } = useStartScrape();

  const [brandId, setBrandId] = useState<null | number>(null);
  const [jobId, setJobId] = useState<null | number>(null);
  const navigatedRef = useRef(false);

  // Poll brand status on step 3
  const { data: brandData } = useGetBrandById(
    stepper.step === 3 ? (brandId ?? 0) : 0,
    { pollWhileExtracting: true },
  );

  // Navigate when analysis completes
  useEffect(() => {
    if (!brandData || !brandId || navigatedRef.current) return;
    const status = brandData.analysisStatus;

    if (status !== "completed" && status !== "failed") return;

    navigatedRef.current = true;
    navigate({
      search: {},
      to: `/brands/${brandId}/overview`,
    });
  }, [brandData?.analysisStatus, brandId, navigate]);

  // Step 1: start scrape if URL provided, advance to step 2
  const handleStep1Next = useCallback(async () => {
    const raw = form.getValues("brandWebAddress")?.trim();
    const url = raw ? normalizeUrl(raw) : raw;

    try {
      if (url) {
        const result = await startScrape({ projectId, websiteUrl: url });

        setJobId(result.jobId);
      }
      stepper.nextStep();
    } catch (error) {
      toast.add(
        {
          description:
            error instanceof Error ? error.message : "Failed to start scrape.",
          title: "Error",
          type: "error",
        },
        { timeout: 5000 },
      );
    }
  }, [form, startScrape, stepper]);

  // Step 2: create brand with attachment IDs, advance to step 3 or navigate to edit
  const handleStep2Next = useCallback(async () => {
    const brandName = form.getValues("brandName");
    const rawAddress = form.getValues("brandWebAddress")?.trim();
    const brandWebAddress = rawAddress ? normalizeUrl(rawAddress) : rawAddress;
    const logoS3Key = form.getValues("logoS3Key");
    const guidelineS3Key = form.getValues("guidelineS3Key");

    try {
      const brand = await createBrand({
        data: {
          basicInfo: {
            audience: null,
            industry: null,
            name: brandName,
            webAddress: brandWebAddress || null,
          },
          guidelineS3Key: guidelineS3Key ?? undefined,
          logoS3Key: logoS3Key ?? undefined,
          profilingJobId: jobId ?? undefined,
          socialLinks: [],
        },
        projectId,
      });

      setBrandId(brand.id);

      const hasAsyncWork = !!jobId || !!guidelineS3Key;

      if (hasAsyncWork) {
        stepper.nextStep();
      } else {
        navigate({
          search: {},
          to: `/brands/${brand.id}/overview`,
        });
      }
    } catch (error) {
      toast.add(
        {
          description:
            error instanceof Error ? error.message : "Failed to create brand.",
          title: "Error",
          type: "error",
        },
        { timeout: 5000 },
      );
    }
  }, [createBrand, form, jobId, navigate, projectId, stepper]);

  return {
    handleStep1Next,
    handleStep2Next,
  };
}
